import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtCAD } from "@/lib/billing";

export default function RefundDialog({ txn, userId, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const remaining = txn ? Math.round((Number(txn.total_cad) - Number(txn.refunded_cad || 0)) * 100) / 100 : 0;

  useEffect(() => {
    if (txn) { setAmount(String(remaining)); setReason(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txn?.id]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/billing/users/${userId}/refund`, { transaction_id: txn.id, amount_cad: Number(amount), reason });
      onDone?.();
    } catch (e) {
      toast.error("Refund failed", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!txn} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="refund-dialog">
        <DialogHeader>
          <DialogTitle>Refund {txn?.kind === "pack" ? txn?.pack_label : "subscription"}</DialogTitle>
          <DialogDescription>
            {txn?.method === "card" ? "Refunds go back to the card through Authorize.Net (unsettled charges are voided instead)." : "Manual payment — this records the refund and issues a credit note; return the money outside the app."}
            {txn?.kind === "pack" ? " Credits bought with this pack are clawed back proportionally." : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Amount (CAD) · up to {fmtCAD(remaining)}</Label>
            <Input type="number" min={0.01} max={remaining} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 min-h-[44px]" data-testid="refund-amount" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Reason (required)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Charged twice" className="mt-1 min-h-[44px]" data-testid="refund-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="refund-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !reason.trim() || !(Number(amount) > 0) || Number(amount) > remaining + 1e-9} className="text-white" style={{ background: "hsl(var(--brand))" }} data-testid="refund-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Refund {fmtCAD(Number(amount) || 0)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
